import { sendGAEvent } from "@next/third-parties/google";

import { isGAEnabled, isGoogleAdsEnabled } from "../env";
import { isClient } from "../utils";

/**
 * Track an event in Google Analytics.
 */
export function trackGAEvent(
  eventName: string,
  properties: Record<string, unknown> = {},
): void {
  if (!isGAEnabled() || !isClient()) return;

  sendGAEvent("event", eventName, properties);
}

/**
 * Set user ID in Google Analytics.
 * This should be called when a user logs in or when their ID is available.
 *
 * @param userId - The unique user ID (UUID, not PII)
 */
export function setGAUserId(userId: string): void {
  if (!isGAEnabled() || !isClient()) return;

  const gaId = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID;
  if (!gaId || !window.gtag) return;

  window.gtag("set", { user_id: userId });
}

/**
 * Set user properties in Google Analytics.
 * These properties will be attached to all subsequent events.
 * IMPORTANT: Do not send PII (Personally Identifiable Information).
 *
 * @param properties - User properties object (e.g., { subscription_plan_tier: "pro" })
 */
export function setGAUserProperties(properties: Record<string, unknown>): void {
  if (!isGAEnabled() || !isClient() || !window.gtag) return;

  window.gtag("set", { user_properties: properties });
}

/**
 * Identify a user in Google Analytics with safe, non-PII data.
 * This function sets both user_id and user properties in GA4.
 * It only accepts safe data (no email, names, or other PII).
 *
 * @param userId - The unique user ID (UUID, not PII)
 * @param properties - Safe user properties (subscription info, etc.)
 *                    Do NOT include: email, firstName, lastName, or any PII
 */
export function identifyGAUser(
  userId: string,
  properties: {
    business_type?: string;
    other_payment_methods?: string[];
    has_stripe_account?: boolean;
    sub_plan_tier?: string;
    sub_billing_period?: string;
    sub_status?: string;
  } = {},
): void {
  if (!isGAEnabled() || !isClient()) return;

  setGAUserId(userId);
  if (Object.keys(properties).length > 0) {
    setGAUserProperties(properties);
  }
}

/**
 * Fire a Google Ads conversion event (TRA-826).
 * Configures the Ads destination and reports the conversion via the
 * existing sitewide gtag.js loader — no second tag is loaded.
 *
 * @param email - Booking email for enhanced conversions. Google hashes it
 *                client-side before sending; never log or store it here.
 */
export function trackGoogleAdsConversion({
  email,
}: { email?: string } = {}): void {
  if (!isGoogleAdsEnabled() || !isClient() || !window.gtag) return;

  const sendTo = process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_SEND_TO!;
  const conversionId = sendTo.split("/")[0]!;

  window.gtag("config", conversionId, { send_page_view: false });
  if (email) {
    window.gtag("set", "user_data", { email });
  }
  window.gtag("event", "conversion", { send_to: sendTo });
}
